import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useSubmitFeatureRequest } from '@/hooks/useFeatureRequests';

const createSchema = (t: (key: string) => string) =>
  z.object({
    title: z.string().min(3, t('submitFeature.validation.titleMin')).max(100, t('submitFeature.validation.titleMax')),
    description: z.string().max(1000, t('submitFeature.validation.descriptionMax')).optional(),
    category: z.enum(['feed-management', 'optimization', 'analytics', 'integrations', 'ux-ui', 'other']),
  });

/** Handgeschrieben statt `z.infer<ReturnType<typeof createSchema>>`, gleicher
 *  Grund wie in `FeedbackDialog.tsx`: dieses Projekt kompiliert mit
 *  `strict: false` und damit ohne `strictNullChecks`. Dort ist
 *  `undefined extends string` WAHR, und zods `addQuestionMarks` markiert
 *  deshalb JEDE Eigenschaft als optional — auch `title` und `category`, die das
 *  Schema verpflichtend fuehrt. Die abgeleitete Form passte damit nicht mehr auf
 *  `SubmitFeatureRequestInput`. Diese Form MUSS zum Schema oben passen; der
 *  Drift-Pin darunter faengt eine Abweichung, solange sie ausdrueckbar ist. */
type FormValues = {
  title: string;
  description?: string;
  category: 'feed-management' | 'optimization' | 'analytics' | 'integrations' | 'ux-ui' | 'other';
};

/** Siehe `FeedbackDialog.tsx`: `Required<>` auf beiden Seiten raeumt die von
 *  `strictNullChecks: false` erzeugte Optionalitaet weg und vergleicht dann
 *  Schluesselmenge und Werttypen in beide Richtungen. Eine reine
 *  Optionalitaets-Aenderung faengt er bewusst nicht. Rein typseitig, erzeugt
 *  also keinen Modul-Code; der Abweichungsfall liefert `false` und nicht
 *  `never`, weil `never extends true` wahr ist und der Pin sonst ein Placebo
 *  waere. */
type ExactExceptOptionality<A, B> = [Required<A>] extends [Required<B>]
  ? ([Required<B>] extends [Required<A>] ? true : false)
  : false;
type AssertTrue<T extends true> = T;
type FormValuesMatchSchema = AssertTrue<
  ExactExceptOptionality<FormValues, z.infer<ReturnType<typeof createSchema>>>
>;

interface SubmitFeatureDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SubmitFeatureDialog({ open, onClose }: SubmitFeatureDialogProps) {
  const { t } = useTranslation('roadmap');
  const submitMutation = useSubmitFeatureRequest();

  const schema = createSchema(t);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      category: 'other',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await submitMutation.mutateAsync(values);
    } catch {
      // A7O-16: Toast kommt aus useSubmitFeatureRequest (onError), der Dialog bleibt offen.
      return;
    }
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('submitFeature.title')}</DialogTitle>
          <DialogDescription>
            {t('submitFeature.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('submitFeature.titleLabel')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('submitFeature.titlePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('submitFeature.descriptionLabel')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('submitFeature.descriptionPlaceholder')}
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('submitFeature.categoryLabel')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('submitFeature.categoryPlaceholder')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="feed-management">{t('features.categories.feed_management')}</SelectItem>
                      <SelectItem value="optimization">{t('features.categories.optimization')}</SelectItem>
                      <SelectItem value="analytics">{t('features.categories.analytics')}</SelectItem>
                      <SelectItem value="integrations">{t('features.categories.integrations')}</SelectItem>
                      <SelectItem value="ux-ui">{t('features.categories.ux_ui')}</SelectItem>
                      <SelectItem value="other">{t('features.categories.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('submitFeature.cancel')}
              </Button>
              <Button type="submit" disabled={submitMutation.isPending}>
                {submitMutation.isPending ? t('submitFeature.submitting') : t('submitFeature.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
